import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Key, Copy, AlertCircle, CheckCircle, Clock, Shield } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface JwtPayload {
  [key: string]: any;
}

interface DecodedJwt {
  header: any;
  payload: JwtPayload;
  signature: string;
  isValid: boolean;
  isExpired: boolean;
  expiresAt?: Date;
  issuedAt?: Date;
  notBefore?: Date;
}

export const JwtDecoderTool: React.FC = () => {
  const { t } = useTranslation();
  const [jwtToken, setJwtToken] = useState('');
  const [decodedJwt, setDecodedJwt] = useState<DecodedJwt | null>(null);
  const [error, setError] = useState('');

  const base64UrlDecode = (str: string): string => {
    try {
      // Add padding if needed
      const padded = str + '='.repeat((4 - str.length % 4) % 4);
      // Replace URL-safe characters
      const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
      return atob(base64);
    } catch (err) {
      throw new Error('Invalid base64url encoding');
    }
  };

  const decodeJwt = (token: string): DecodedJwt => {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format. JWT must have 3 parts separated by dots.');
    }

    const [headerB64, payloadB64, signature] = parts;

    // Decode header
    const headerJson = base64UrlDecode(headerB64);
    const header = JSON.parse(headerJson);

    // Decode payload
    const payloadJson = base64UrlDecode(payloadB64);
    const payload = JSON.parse(payloadJson);

    // Check expiration
    const now = Math.floor(Date.now() / 1000);
    const isExpired = payload.exp ? payload.exp < now : false;
    const expiresAt = payload.exp ? new Date(payload.exp * 1000) : undefined;
    const issuedAt = payload.iat ? new Date(payload.iat * 1000) : undefined;
    const notBefore = payload.nbf ? new Date(payload.nbf * 1000) : undefined;

    // Basic validation (structure only, not cryptographic)
    const isValid = header && payload && signature;

    return {
      header,
      payload,
      signature,
      isValid,
      isExpired,
      expiresAt,
      issuedAt,
      notBefore
    };
  };

  const handleDecode = () => {
    if (!jwtToken.trim()) {
      setError(t('common.enterJwtToken'));
      setDecodedJwt(null);
      return;
    }

    try {
      const decoded = decodeJwt(jwtToken.trim());
      setDecodedJwt(decoded);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.failedToDecodeJwt'));
      setDecodedJwt(null);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copiedToClipboard'));
    } catch (err) {
      toast.error(t('common.failedToCopyToClipboard'));
    }
  };

  const formatJson = (obj: any): string => {
    return JSON.stringify(obj, null, 2);
  };

  const getTokenStatus = () => {
    if (!decodedJwt) return null;
    
    if (!decodedJwt.isValid) {
      return { icon: AlertCircle, color: 'text-red-600', text: 'Invalid Token' };
    }
    
    if (decodedJwt.isExpired) {
      return { icon: AlertCircle, color: 'text-red-600', text: 'Token Expired' };
    }
    
    return { icon: CheckCircle, color: 'text-green-600', text: 'Valid Token' };
  };

  // Auto-decode when token changes
  useEffect(() => {
    if (jwtToken.trim()) {
      const timeoutId = setTimeout(() => {
        handleDecode();
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setDecodedJwt(null);
      setError('');
    }
  }, [jwtToken]);

  const sampleJwt = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyLCJleHAiOjE5MTYyMzkwMjJ9.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c';

  const status = getTokenStatus();

  return (
    <div className="space-y-6">
      {/* JWT Input */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Key className="w-5 h-5" />
            <span>JWT Decoder</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                JWT Token
              </label>
              <textarea
                value={jwtToken}
                onChange={(e) => setJwtToken(e.target.value)}
                placeholder="Paste your JWT token here..."
                className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white font-mono text-sm"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4" />
                  <span>{error}</span>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleDecode}
                variant="primary"
                disabled={!jwtToken.trim()}
              >
                Decode JWT
              </Button>
              <Button
                onClick={() => setJwtToken(sampleJwt)}
                variant="outline"
              >
                Load Sample JWT
              </Button>
              <Button
                onClick={() => {
                  setJwtToken('');
                  setDecodedJwt(null);
                  setError('');
                }}
                variant="outline"
              >
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Token Status */}
      {decodedJwt && status && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <status.icon className={`w-5 h-5 ${status.color}`} />
              <span>Token Status</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className={`text-lg font-bold ${status.color}`}>
                  {status.text}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Status</div>
              </div>
              
              {decodedJwt.expiresAt && (
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {decodedJwt.expiresAt.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Expires At</div>
                </div>
              )}
              
              {decodedJwt.issuedAt && (
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {decodedJwt.issuedAt.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Issued At</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Decoded Content */}
      {decodedJwt && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Header */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Header</span>
                <Button
                  onClick={() => copyToClipboard(formatJson(decodedJwt.header))}
                  variant="outline"
                  size="sm"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg overflow-auto text-sm font-mono">
                {formatJson(decodedJwt.header)}
              </pre>
            </CardContent>
          </Card>

          {/* Payload */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Payload</span>
                <Button
                  onClick={() => copyToClipboard(formatJson(decodedJwt.payload))}
                  variant="outline"
                  size="sm"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg overflow-auto text-sm font-mono">
                {formatJson(decodedJwt.payload)}
              </pre>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payload Claims Explanation */}
      {decodedJwt && (
        <Card>
          <CardHeader>
            <CardTitle>Common JWT Claims</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Standard Claims:</h4>
                <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                  <li>• <strong>iss</strong> - Issuer (who issued the token)</li>
                  <li>• <strong>sub</strong> - Subject (who the token is about)</li>
                  <li>• <strong>aud</strong> - Audience (who should accept the token)</li>
                  <li>• <strong>exp</strong> - Expiration time (when token expires)</li>
                  <li>• <strong>iat</strong> - Issued at (when token was issued)</li>
                  <li>• <strong>nbf</strong> - Not before (token not valid before this time)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Security Notes:</h4>
                <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                  <li>• This tool only decodes JWT structure</li>
                  <li>• Signature verification requires the secret key</li>
                  <li>• Never share JWT tokens containing sensitive data</li>
                  <li>• Always validate tokens on the server side</li>
                  <li>• Check expiration times before using tokens</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
